import React, { useState } from 'react';
import { db } from '../firebase/config';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { XCircle, AlertTriangle, X } from 'lucide-react';

const CANCEL_REASONS = [
    { key: 'change_of_mind', label: '단순 변심' },
    { key: 'delivery_delay', label: '배송 지연' },
    { key: 'info_mismatch', label: '상품 정보 불일치' },
    { key: 'price_change', label: '가격 변동' },
    { key: 'other', label: '기타' }
];

function CancelRequestModal({ order, userInfo, onClose, onSuccess }) {
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!selectedReason) {
            setError('취소 사유를 선택해주세요.');
            return;
        }

        if (selectedReason === 'other' && !customReason.trim()) {
            setError('기타 사유를 입력해주세요.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const reasonObj = CANCEL_REASONS.find(r => r.key === selectedReason);
            const cancelReasonText = selectedReason === 'other'
                ? customReason.trim()
                : reasonObj.label;

            const orderId = order.id || order.orderId;
            const orderRef = doc(db, 'marketplaceOrders', orderId);

            await updateDoc(orderRef, {
                status: 'cancel_requested',
                cancelReason: cancelReasonText,
                cancelRequestedAt: serverTimestamp()
            });

            if (onSuccess) onSuccess();
            if (onClose) onClose();
        } catch (err) {
            console.error('취소 요청 실패:', err);
            setError('취소 요청에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <XCircle size={24} color="#6366f1" />
                        <h2 style={styles.headerTitle}>주문 취소 요청</h2>
                    </div>
                    <button style={styles.closeBtn} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {error && (
                        <div style={styles.errorBox}>
                            <AlertTriangle size={18} color="#ef4444" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Order Info Summary */}
                    <div style={styles.orderSummary}>
                        <span style={styles.orderSummaryLabel}>주문번호</span>
                        <span style={styles.orderSummaryValue}>{order.id || order.orderId}</span>
                    </div>

                    {/* Warning Notice */}
                    <div style={styles.warningBox}>
                        <AlertTriangle size={18} color="#d97706" />
                        <p style={styles.warningText}>
                            취소 요청 후 판매자 확인이 완료되면 취소가 처리됩니다.
                            결제 금액은 원결제 수단으로 환불됩니다.
                        </p>
                    </div>

                    {/* Cancel Reason Selection */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>취소 사유 선택</label>
                        <div style={styles.reasonOptions}>
                            {CANCEL_REASONS.map((reason) => (
                                <button
                                    key={reason.key}
                                    style={{
                                        ...styles.reasonButton,
                                        ...(selectedReason === reason.key ? styles.reasonButtonActive : {})
                                    }}
                                    onClick={() => {
                                        setSelectedReason(reason.key);
                                        setError('');
                                    }}
                                >
                                    <div style={{
                                        ...styles.radioCircle,
                                        ...(selectedReason === reason.key ? styles.radioCircleActive : {})
                                    }}>
                                        {selectedReason === reason.key && (
                                            <div style={styles.radioInner} />
                                        )}
                                    </div>
                                    <span style={{
                                        ...styles.reasonLabel,
                                        color: selectedReason === reason.key ? '#6366f1' : '#374151'
                                    }}>
                                        {reason.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Reason Input (for "기타") */}
                    {selectedReason === 'other' && (
                        <div style={styles.fieldGroup}>
                            <label style={styles.label}>기타 사유 입력</label>
                            <textarea
                                value={customReason}
                                onChange={(e) => {
                                    setCustomReason(e.target.value);
                                    setError('');
                                }}
                                placeholder="취소 사유를 입력해주세요"
                                style={styles.textarea}
                                maxLength={500}
                                rows={4}
                            />
                            <span style={styles.charCount}>{customReason.length}/500</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <button style={styles.cancelBtn} onClick={onClose} disabled={submitting}>
                        닫기
                    </button>
                    <button
                        style={{
                            ...styles.submitBtn,
                            ...(submitting ? styles.submitBtnDisabled : {})
                        }}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? '요청 중...' : '취소 요청'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000
    },
    modal: {
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    headerTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a'
    },
    closeBtn: {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '24px'
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '10px',
        fontSize: '14px',
        color: '#ef4444',
        marginBottom: '20px'
    },
    orderSummary: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px'
    },
    orderSummaryLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b'
    },
    orderSummaryValue: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#0f172a'
    },
    warningBox: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '14px 16px',
        backgroundColor: '#fffbeb',
        borderRadius: '10px',
        border: '1px solid #fde68a',
        marginBottom: '20px'
    },
    warningText: {
        margin: 0,
        fontSize: '13px',
        color: '#92400e',
        lineHeight: '1.6',
        flex: 1
    },
    fieldGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    reasonOptions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
    },
    reasonButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 16px',
        backgroundColor: '#f8fafc',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.2s'
    },
    reasonButtonActive: {
        backgroundColor: '#eef2ff',
        borderColor: '#6366f1'
    },
    radioCircle: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        border: '2px solid #cbd5e1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s'
    },
    radioCircleActive: {
        borderColor: '#6366f1'
    },
    radioInner: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: '#6366f1'
    },
    reasonLabel: {
        fontSize: '15px',
        fontWeight: '500'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        color: '#0f172a',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box',
        resize: 'vertical',
        fontFamily: 'inherit',
        lineHeight: '1.6',
        transition: 'border-color 0.2s'
    },
    charCount: {
        display: 'block',
        textAlign: 'right',
        marginTop: '4px',
        fontSize: '12px',
        color: '#94a3b8'
    },
    footer: {
        display: 'flex',
        gap: '12px',
        padding: '20px 24px',
        borderTop: '1px solid #e2e8f0'
    },
    cancelBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    submitBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s'
    },
    submitBtnDisabled: {
        backgroundColor: '#a5b4fc',
        cursor: 'not-allowed'
    }
};

export default CancelRequestModal;
